/**
 * WB4.1 P79-5 — session-scoped single-depth undo snapshot (toast undo v1).
 */

import { getWorkbenchRecoverabilityLadderEntry } from './workbench-recoverability-ladder';

export const WORKBENCH_UNDO_V1_DEFAULT_TTL_MS =
  getWorkbenchRecoverabilityLadderEntry('low').undoTtlMs ?? 8_000;

export interface WorkbenchUndoV1Snapshot {
  id: string;
  tenantId: string;
  objectId: string;
  businessDomain: string;
  fieldKey: string;
  previousValue: string;
  nextValue: string;
  createdAt: string;
  expiresAt: string;
  restore: () => Promise<void>;
}

let currentSnapshot: WorkbenchUndoV1Snapshot | null = null;

function createSnapshotId(): string {
  return `wbx-undo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function setWorkbenchUndoSnapshot(input: {
  tenantId: string;
  objectId: string;
  businessDomain: string;
  fieldKey: string;
  previousValue: string;
  nextValue: string;
  restore: () => Promise<void>;
  ttlMs?: number;
}): WorkbenchUndoV1Snapshot {
  const createdAt = new Date().toISOString();
  const ttlMs = input.ttlMs ?? WORKBENCH_UNDO_V1_DEFAULT_TTL_MS;
  const snapshot: WorkbenchUndoV1Snapshot = {
    id: createSnapshotId(),
    tenantId: input.tenantId,
    objectId: input.objectId,
    businessDomain: input.businessDomain,
    fieldKey: input.fieldKey,
    previousValue: input.previousValue,
    nextValue: input.nextValue,
    createdAt,
    expiresAt: new Date(Date.now() + ttlMs).toISOString(),
    restore: input.restore,
  };
  currentSnapshot = snapshot;
  return snapshot;
}

export function getWorkbenchUndoSnapshot(): WorkbenchUndoV1Snapshot | null {
  if (!currentSnapshot) {
    return null;
  }
  if (!isWorkbenchUndoSnapshotActive(currentSnapshot)) {
    currentSnapshot = null;
    return null;
  }
  return currentSnapshot;
}

export function clearWorkbenchUndoSnapshot(): void {
  currentSnapshot = null;
}

export function isWorkbenchUndoSnapshotActive(snapshot: WorkbenchUndoV1Snapshot): boolean {
  const expiresAt = Date.parse(snapshot.expiresAt);
  if (Number.isNaN(expiresAt)) {
    return false;
  }
  return Date.now() < expiresAt;
}

export async function executeWorkbenchUndoV1(): Promise<boolean> {
  const snapshot = getWorkbenchUndoSnapshot();
  if (!snapshot) {
    return false;
  }
  await snapshot.restore();
  clearWorkbenchUndoSnapshot();
  return true;
}

/** Test-only reset */
export function clearWorkbenchUndoV1Store(): void {
  currentSnapshot = null;
}
